import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { Button, Dropdown, Spin } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { RequestDataResponse } from 'core/dashboard/types';
import './dashboard.scss';
import { SCORCHSOFT_USER, SECURITY_USER, ADMIN_ACCESS_LEVELS, SUPER_ADMIN_USER, REQUEST_TYPE_VISITOR, REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING, VISIT_REQUEST_STATUS_ID } from 'core/utils/value';
import { AddIcon, TriangleDownIcon } from 'components/Elements/CustomSVGIcon';
import { LeftOutlined } from '@ant-design/icons';
import { cancelAmendmentRequest } from 'core/visitRequestForm/visitRequestFormApi';
import { renderApprover, renderDateSubmitted, renderPeriod, renderRefNumber, renderRequestType, renderRequester, renderSite } from 'components/RequestTable/CellRenderers';
import { Modal } from 'antd/lib';

import { isArray, isEmpty, isNull } from 'underscore';
import { resetLockedStatus } from 'core/visitRequestReview/visitRequestReviewSlice';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import { AppDispatch, CombinedStates } from '../../core/types';
import { dashboardFilterRequest, exportCompletedVisits } from '../../core/dashboard/dashboardApi';
import SearchFilter from './components/SearchFilter';
import RequestTable from './components/RequestTable';
import Main from '../../components/layout/Main';
import HourlyRateModal, { ExportParams } from './components/HourlyRateModal';
import { DashboardFilterParams, FilterModalValues, FilterOptions, ExportType } from './types';
import LockedRequestModal from './components/OverruledModal';
import FilterModal from './components/FilterModal';
import GenerateBookingLinkModal from './components/GenerateBookingLinkModal';
import NewUpdateBanner from './components/NewUpdateBanner';

const Dashboard: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const { requestData, isFetching, isExporting, summaryData, filterData, pagination } = useSelector((state: CombinedStates) => state.dashboard);
    const { lockedStatus: { overriden, lockedBy, requestId } } = useSelector((state: CombinedStates) => state.visitRequestReview);
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { isCreating, lastRequestAmended } = useSelector((state: CombinedStates) => state.visitRequestForm);

    const [statusList, setStatusList] = useState<object>({});
    const [siteList, setSiteList] = useState<Array<{ name: string; id: number }>>([]);
    const [sortedData, setSortedData] = useState<RequestDataResponse[] | null>(null);
    const [sortDirection, setSortDirection] = useState(queryParams.get('sort') || null);
    const [sortField, setSortField] = useState<string | null>(queryParams.get('sortField') || null);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
    const [showBookingLinkModal, setShowBookingLinkModal] = useState<boolean>(false);
    const [reportsMenuOpen, setReportsMenuOpen] = useState<boolean>(false);

    // Completed-visits export stays restricted to super/scorchsoft admins (the
    // same gate the old standalone button used); the report views are open to
    // all admins.
    const canExportCompletedVisits = userData?.role?.accessLevel === SUPER_ADMIN_USER
        || userData?.role?.accessLevel === SCORCHSOFT_USER;

    // Match the existing status/site filter dropdown item styling.
    const reportMenuLabel = (text: string) => (
        <div className="item-dropdown">
            <div className="item-title-dropdown">{text}</div>
        </div>
    );

    // Combined "Reports & Exports" menu (per Iftab) so the on-demand report
    // views, the one-off booking link and the completed-visits export live
    // behind a single admin button instead of taking several toolbar slots.
    const actionMenuItems: MenuProps['items'] = [
        { key: 'ad-hoc', label: reportMenuLabel('View Ad Hoc Bookings'), onClick: () => navigate('/ad-hoc-bookings') },
        { key: 'dal', label: reportMenuLabel('View DAL'), onClick: () => navigate('/daily-access-list') },
        { type: 'divider' },
        { key: 'booking-link', label: reportMenuLabel('Generate One-Off Booking Link'), onClick: () => setShowBookingLinkModal(true) },
        ...(canExportCompletedVisits
            ? [{ key: 'export', label: reportMenuLabel('Export Completed Visits'), onClick: () => setShowExportModal(true) }]
            : []),
    ];
    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    const getArrayQueryParam = (param: string | null): Array<string> => (param ? param.split(',') : []);

    const [searchTerms, setSearchTerms] = useState<string | Array<string>>(
        getArrayQueryParam(queryParams.get('searchTerms')).map((term) => decodeURIComponent(term)),
    );
    const [startDate, setStartDate] = useState<string>(queryParams.get('startDate') || '');
    const [endDate, setEndDate] = useState<string>(queryParams.get('endDate') || '');
    const [siteText, setSiteText] = useState<string[]>(getArrayQueryParam(queryParams.get('siteText')));

    // Drafts are private in-progress records and soft-deleted is an
    // internal lifecycle state the backend never returns rows for, so
    // neither belongs in the dashboard filter at all. Completed and
    // Cancelled stay selectable in the dropdown (users may want to
    // look back at finished or cancelled work) but are excluded from
    // the default selection so a fresh landing shows active/in-flight
    // work, not historical noise.
    const HIDDEN_STATUS_KEYS: string[] = [
        VISIT_REQUEST_STATUS_ID.DRAFT,
        VISIT_REQUEST_STATUS_ID.SOFT_DELETED,
    ];
    const DEFAULT_EXCLUDED_STATUS_KEYS: string[] = [
        VISIT_REQUEST_STATUS_ID.COMPLETED,
        VISIT_REQUEST_STATUS_ID.CANCELLED,
    ];
    // The status keys to pre-tick when the URL doesn't already specify a
    // statusText filter. Derived from the canonical id map so the set
    // stays in sync if new statuses are added to VISIT_REQUEST_STATUS_ID.
    const DEFAULT_INITIAL_STATUS_KEYS = Object.values(VISIT_REQUEST_STATUS_ID)
        .filter((key) => !HIDDEN_STATUS_KEYS.includes(key) && !DEFAULT_EXCLUDED_STATUS_KEYS.includes(key));

    // Strip hidden keys from any bookmarked / legacy URL so a stale
    // `?statusText=0,…` (Draft) can't slip past the dropdown changes and
    // reintroduce hidden statuses into the table.
    const initialStatusFromUrl = getArrayQueryParam(queryParams.get('statusText'))
        .filter((key) => !HIDDEN_STATUS_KEYS.includes(key));
    const [statusText, setStatusText] = useState<string[]>(
        initialStatusFromUrl.length > 0 ? initialStatusFromUrl : DEFAULT_INITIAL_STATUS_KEYS,
    );
    const [referenceNumber, setReferenceNumber] = useState<string[]>(getArrayQueryParam(queryParams.get('referenceNumber')));
    const [requesterName, setRequesterName] = useState<string[]>(getArrayQueryParam(queryParams.get('requesterName')));
    const [approverName, setApproverName] = useState<string[]>(getArrayQueryParam(queryParams.get('approverName')));
    const [dateSubmittedStart, setDateSubmittedStart] = useState<string>(queryParams.get('dateSubmittedStart') || '');
    const [dateSubmittedEnd, setDateSubmittedEnd] = useState<string>(queryParams.get('dateSubmittedEnd') || '');
    const [requestType, setRequestType] = useState<string[]>(getArrayQueryParam(queryParams.get('requestType')));
    const [requesterType, setRequesterType] = useState<string[]>(getArrayQueryParam(queryParams.get('requesterType')));

    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];
    const backLink = (
        <Button type="link" onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    // Derive filter options from filterData returned by backend
    const filterOptions: FilterOptions = useMemo(() => {
        const refNumbers = [...(filterData?.referenceNumbers || [])]
            .sort((a, b) => a - b)
            .map(ref => ({ value: String(ref), label: String(ref).padStart(3, '0') }));

        const requesterNames = [...(filterData?.requesterNames || [])]
            .sort()
            .map(name => ({ value: name, label: name }));

        const approverNames = [...(filterData?.approverNames || [])]
            .sort()
            .map(name => ({ value: name, label: name }));

        const sites = siteList.map(s => ({ value: s.name, label: s.name }));

        const statuses = Object.entries(statusList).map(([key, val]) => ({
            value: key,
            label: val as string,
        }));

        const requestTypes = [
            { value: REQUEST_TYPE_VISITOR, label: 'Visitor Request' },
            { value: REQUEST_TYPE_ACCESS, label: 'Access Request' },
            { value: REQUEST_TYPE_GUARDING, label: 'Guarding Request' },
        ];

        // Guest bookings come in through a booking link and have no account
        // behind them, so they are worth isolating from normal requests.
        const requesterTypes = [
            { value: 'registered', label: 'Registered User' },
            { value: 'guest', label: 'Guest Booking' },
        ];

        return {
            referenceNumbers: refNumbers,
            requesterNames,
            approverNames,
            sites,
            statuses,
            requestTypes,
            requesterTypes,
        };
    }, [filterData, siteList, statusList]);

    // Current filter values for the modal
    const currentFilterValues: FilterModalValues = useMemo(() => ({
        referenceNumber,
        site: siteText,
        requesterName,
        startDate,
        endDate,
        statusText,
        dateSubmittedStart,
        dateSubmittedEnd,
        approverName,
        requestType,
        requesterType,
    }), [referenceNumber, siteText, requesterName, startDate, endDate, statusText, dateSubmittedStart, dateSubmittedEnd, approverName, requestType, requesterType]);

    useEffect(() => {
        if (userData?.role?.accessLevel === SECURITY_USER) {
            navigate('/security-dashboard');
        }

        if (lastRequestAmended !== undefined && lastRequestAmended !== null) {
            dispatch(cancelAmendmentRequest({ id: lastRequestAmended.id, type: lastRequestAmended.type }));
        }
    }, []);

    useEffect(() => {
        if (filterData) {
            const statuses = filterData?.statuses as Record<string, string> | undefined;
            const visibleStatuses: Record<string, string> = {};
            if (statuses) {
                Object.entries(statuses).forEach(([key, value]) => {
                    if (!HIDDEN_STATUS_KEYS.includes(key)) {
                        visibleStatuses[key] = value;
                    }
                });
            }
            setStatusList(visibleStatuses);
            setSiteList(filterData?.sites as Array<{ name: string; id: number }>);
        }
    }, [filterData]);

    const handleOverruleModalClose = () => {
        dispatch(resetLockedStatus());
    };

    const handleSubmitRequest = (page = 1, listOnly = false) => {
        dispatch(dashboardFilterRequest({
            listOnly: listOnly || undefined,
            q: searchTerms,
            status: statusText,
            site: siteText,
            startDate,
            endDate,
            page,
            sort: sortDirection,
            sortField,
            referenceNumber: referenceNumber.length > 0 ? referenceNumber : undefined,
            requesterName: requesterName.length > 0 ? requesterName : undefined,
            approverName: approverName.length > 0 ? approverName : undefined,
            dateSubmittedStart: dateSubmittedStart || undefined,
            dateSubmittedEnd: dateSubmittedEnd || undefined,
            requestType: requestType.length > 0 ? requestType : undefined,
            requesterType: requesterType.length > 0 ? requesterType : undefined,
        }));
    };

    const requestCard = [
        {
            id: 1,
            number: summaryData?.pending,
            title: 'Pending Approval',
            statusFilter: ['3', '5', '10'],
        },
        {
            id: 2,
            number: summaryData?.rejectedQueried,
            title: 'Rejected or Queried',
            statusFilter: ['50', '60'],
        },
        {
            id: 3,
            number: summaryData?.approved,
            title: 'Approved',
            statusFilter: ['20', '30'],
        },
        {
            id: 4,
            number: summaryData?.completed,
            title: 'Completed',
            statusFilter: ['40'],
        },
    ];

    const onColumnSort = (field: string) => {
        let newDirection: string | null = 'asc';
        if (sortField === field) {
            if (sortDirection === 'asc') {
                newDirection = 'desc';
            } else if (sortDirection === 'desc') {
                newDirection = null;
                setSortField(null);
                setSortDirection(null);
                return;
            }
        }
        setSortField(field);
        setSortDirection(newDirection);
    };

    const renderSortableHeader = (title: string, field: string) => (
        <div
            onClick={() => onColumnSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header">
            {title}
            <span className="sort-indicator">
                {sortField === field && (
                    sortDirection === 'asc' ? ' ↑' : ' ↓'
                )}
            </span>
        </div>
    );

    const tableColumns = [
        {
            title: renderSortableHeader('Ref #', 'id'),
            dataIndex: 'ref',
            key: 'ref',
            width: 100,
            render: (ref: number) => renderRefNumber(ref),
        },
        {
            title: 'Request Type',
            dataIndex: 'type',
            key: 'type',
            width: 170,
            render: (_:undefined, record:RequestDataResponse) => renderRequestType(_, record),
        },
        {
            title: renderSortableHeader('Site', 'site'),
            dataIndex: 'site.address',
            key: 'site.address',
            render: (_:undefined, record:RequestDataResponse) => renderSite(_, record),
        },
        {
            title: renderSortableHeader('Requester', 'requester'),
            dataIndex: 'requester',
            key: 'requester',
            width: 150,
            render: (_:undefined, record:RequestDataResponse) => renderRequester(_, record),
        },
        {
            title: renderSortableHeader('Requested Period', 'requestedPeriod'),
            dataIndex: 'requestedPeriod',
            key: 'requestedPeriod',
            render: (_:undefined, record:RequestDataResponse) => renderPeriod(_, record),
        },
        {
            title: renderSortableHeader('Status', 'status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
        },
        {
            title: renderSortableHeader('Date Submitted', 'date'),
            dataIndex: 'dateSubmitted',
            key: 'dateSubmitted',
            render: (_:undefined, record:RequestDataResponse) => renderDateSubmitted(_, record),
        },
        {
            title: renderSortableHeader('Approver', 'approver'),
            dataIndex: 'approver',
            key: 'approver',
            render: (_:undefined, record:RequestDataResponse) => renderApprover(_, record, userData),
        },
    ];

    const updateSearchFilterParams = (params: DashboardFilterParams) => {
        setSearchTerms(params.searchTerms);
        setStartDate(params.startDate);
        setEndDate(params.endDate);
        setSiteText(params.siteText);
        setStatusText(params.statusText);
        if (params.referenceNumber) setReferenceNumber(params.referenceNumber);
        if (params.requesterName) setRequesterName(params.requesterName);
        if (params.approverName) setApproverName(params.approverName);
        if (params.dateSubmittedStart !== undefined) setDateSubmittedStart(params.dateSubmittedStart);
        if (params.dateSubmittedEnd !== undefined) setDateSubmittedEnd(params.dateSubmittedEnd);
        if (params.requestType) setRequestType(params.requestType);
        if (params.requesterType) setRequesterType(params.requesterType);
    };

    const handleCreateNewRequest = () => {
        navigate('/sites');
    };

    const handleCardClick = (statusFilter: string[]) => {
        setStatusText(statusFilter);
    };

    const handleQueryChange = (state: string | Array<string>, queryKey: string) => {
        if (typeof state === 'string') {
            if (isNull(state) || isEmpty(state)) {
                queryParams.delete(queryKey);
            } else {
                queryParams.set(queryKey, state);
            }
        } else if (isArray(state)) {
            if (isNull(state) || isEmpty(state)) {
                queryParams.delete(queryKey);
            } else {
                queryParams.set(queryKey, state.toString());
            }
        }
    };

    const onExport = (params: ExportParams) => {
        handleQueryChange(searchTerms, 'searchTerms');
        handleQueryChange(startDate, 'startDate');
        handleQueryChange(endDate, 'endDate');
        handleQueryChange(siteText, 'siteText');

        const exportParams: {
            site: string[];
            q: string | string[];
            startDate?: string;
            endDate?: string;
            exportType: ExportType;
            twoManSite?: string | undefined;
            includeAllBookings?: boolean | undefined;
        } = {
            site: siteText,
            q: searchTerms,
            exportType: 'csv',
            twoManSite: params.twoManSite,
            includeAllBookings: params.includeAllBookings,
        };

        // Use the date range from the modal if the user set either end of it,
        // otherwise fall back to the dashboard's current filter dates. (Requiring
        // BOTH meant a "from" with no "to" was silently dropped.)
        const useModalDates = Boolean(params.startDate || params.endDate);
        const chosenStart = useModalDates ? params.startDate : startDate;
        const chosenEnd = useModalDates ? params.endDate : endDate;
        if (chosenStart) {
            exportParams.startDate = chosenStart;
        }
        if (chosenEnd) {
            exportParams.endDate = chosenEnd;
        }

        // The modal stays open reporting progress and closes itself once the file
        // has downloaded. The export lives in the store, so closing the modal by
        // hand does not stop it — and a failure leaves the modal up (with the
        // error notification) so the export can be retried.
        dispatch(exportCompletedVisits(exportParams))
            .then(unwrapResult)
            .then(() => setShowExportModal(false))
            .catch(() => undefined);
    };

    const handleOpenFilterModal = () => {
        setIsFilterModalOpen(true);
    };

    const handleCloseFilterModal = () => {
        setIsFilterModalOpen(false);
    };

    const handleApplyFilters = (values: FilterModalValues) => {
        setReferenceNumber(values.referenceNumber);
        setSiteText(values.site);
        setRequesterName(values.requesterName);
        setStartDate(values.startDate);
        setEndDate(values.endDate);
        setStatusText(values.statusText);
        setDateSubmittedStart(values.dateSubmittedStart);
        setDateSubmittedEnd(values.dateSubmittedEnd);
        setApproverName(values.approverName);
        setRequestType(values.requestType);
        setRequesterType(values.requesterType);
    };

    const handleClearAllFilters = () => {
        setSearchTerms([]);
        setStartDate('');
        setEndDate('');
        setSiteText([]);
        setStatusText([]);
        setReferenceNumber([]);
        setRequesterName([]);
        setApproverName([]);
        setDateSubmittedStart('');
        setDateSubmittedEnd('');
        setRequestType([]);
        setRequesterType([]);
        setSortedData(null);
        setSortDirection(null);
        setSortField(null);
    };

    useEffect(() => {
        handleQueryChange(searchTerms, 'searchTerms');
        handleQueryChange(startDate, 'startDate');
        handleQueryChange(endDate, 'endDate');
        handleQueryChange(siteText, 'siteText');
        handleQueryChange(statusText, 'statusText');
        handleQueryChange(referenceNumber, 'referenceNumber');
        handleQueryChange(requesterName, 'requesterName');
        handleQueryChange(approverName, 'approverName');
        handleQueryChange(dateSubmittedStart, 'dateSubmittedStart');
        handleQueryChange(dateSubmittedEnd, 'dateSubmittedEnd');
        handleQueryChange(requestType, 'requestType');
        handleQueryChange(requesterType, 'requesterType');
        if (sortDirection) {
            handleQueryChange(sortDirection, 'sort');
        } else {
            queryParams.delete('sort');
        }
        if (sortField) {
            handleQueryChange(sortField, 'sortField');
        } else {
            queryParams.delete('sortField');
        }

        // Reset to page 1 when filters change
        handleSubmitRequest(1);
        navigate({ pathname: '/dashboard', search: queryParams.toString() });
    }, [searchTerms, startDate, endDate, siteText, statusText, sortDirection, sortField, referenceNumber, requesterName, approverName, dateSubmittedStart, dateSubmittedEnd, requestType, requesterType]);

    return (
        <>
            <Modal
                open={showExportModal}
                onCancel={() => setShowExportModal(false)}
                footer={null}
                width={700}
                centered
                // Fresh form on every open: the modal keeps its selections while
                // an export is running rather than clearing them on submit.
                destroyOnClose
                className="availability-modal">
                <HourlyRateModal
                    onCancel={() => setShowExportModal(false)}
                    onExport={onExport}
                    loading={isExporting} />
            </Modal>
            <FilterModal
                visible={isFilterModalOpen}
                onClose={handleCloseFilterModal}
                onApply={handleApplyFilters}
                onClearAll={handleClearAllFilters}
                filterOptions={filterOptions}
                initialValues={currentFilterValues} />
            <Main
                className="login"
                title={<div>Dashboard</div>}
                hideSubHeader
                subHeaderTitle="Dashboard"
                subHeaderHasBackLink
                subHeaderBackLink="/"
                menuWidth={350}
                menuPlacement="right"
                toolbar={toolbar}
                showFooter={false}
                floatingHeader={false}
                footerContent=""
                floatingFooter={false}
                backLink={backLink}
                breadcrumb={navigation}>
                <div className="main-container dashboard">
                    <div>
                        <NewUpdateBanner />
                        <div className="request-card-content">
                            {requestCard.map(card => (
                                <div
                                    key={card.id}
                                    className="request-card clickable"
                                    onClick={() => handleCardClick(card.statusFilter)}
                                    style={{ cursor: 'pointer' }}>
                                    <div className="content">
                                        <span className="request-number">{card.number}</span>
                                        <span className="request-text">{card.title}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="request-button-container">
                            <div className="request-buttons">
                                <Button type="primary" className="create-button" data-tour="dashboard-create-request" onClick={handleCreateNewRequest} loading={isCreating}>
                                    <AddIcon />
                                    <div style={{ padding: 10 }}>Create New Request</div>
                                </Button>
                            </div>
                        </div>
                        <div className="search-container">
                            <SearchFilter
                                statusFilter={statusList}
                                onClear={handleClearAllFilters}
                                searchParams={queryParams}
                                updateFilters={updateSearchFilterParams}
                                currentStatusText={statusText}
                                onOpenFilterModal={handleOpenFilterModal}
                                currentFilters={{
                                    searchTerms: Array.isArray(searchTerms) ? searchTerms : [searchTerms],
                                    startDate,
                                    endDate,
                                    siteText,
                                    statusText,
                                    referenceNumber,
                                    requesterName,
                                    approverName,
                                    dateSubmittedStart,
                                    dateSubmittedEnd,
                                    requestType,
                                    requesterType,
                                }}
                                actions={ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel) ? (
                                    <div className="dashboard-actions">
                                        {isExporting && (
                                            <span className="export-in-progress">
                                                <Spin size="small" />
                                                <span>Exporting...</span>
                                            </span>
                                        )}
                                        <Dropdown
                                            menu={{ items: actionMenuItems }}
                                            overlayClassName="status-dropdown"
                                            placement="bottomRight"
                                            trigger={['click']}
                                            open={reportsMenuOpen}
                                            onOpenChange={setReportsMenuOpen}>
                                            <Button type="primary" className="export-button" style={{ height: 45 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '0 10px' }}>
                                                    <div>Reports &amp; Exports</div>
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        transition: 'transform 0.2s ease',
                                                        transform: reportsMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    }}>
                                                        <TriangleDownIcon />
                                                    </div>
                                                </div>
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ) : null} />
                        </div>
                        <div className="table-container">
                            <RequestTable
                                pagination={{
                                    currentPage: pagination.currentPage,
                                    pageSize: pagination.pageSize,
                                    total: pagination.total,
                                    onChange: (page) => {
                                        handleSubmitRequest(page, true);
                                        // scroll to top
                                        window.scrollTo({ top: 0, behavior: 'auto' });
                                    },
                                }}
                                filters={{
                                    searchTerms,
                                    startDate,
                                    endDate,
                                    siteText,
                                    statusText,
                                    sortDirection,
                                }}
                                loading={isFetching}
                                data={sortedData || requestData}
                                columns={tableColumns} />
                        </div>
                    </div>
                </div>
                <LockedRequestModal
                    isModalOpen={overriden === true}
                    handleCancel={handleOverruleModalClose}
                    lockedBy={lockedBy}
                    requestId={requestId} />
                <GenerateBookingLinkModal
                    open={showBookingLinkModal}
                    onClose={() => setShowBookingLinkModal(false)} />
            </Main>
        </>
    );
};

export default Dashboard;

Dashboard.propTypes = {};
